// Responsiva firmada (PDF escaneado) — módulo aparte de api.ts para no
// interferir con trabajo en curso en ese archivo. Duplica los helpers
// mínimos de auth/errores.
import { acquireIdToken, DEV_BYPASS_AUTH, renovarSesion } from "./auth";

async function authHeaders(): Promise<Record<string, string>> {
  if (DEV_BYPASS_AUTH) return {}; // dev: el backend usa DEV_USER_EMAIL
  const token = await acquireIdToken();
  return { Authorization: `Bearer ${token}` };
}

/** fetch autenticado; si el backend contesta 401 la sesión murió → al login. */
async function fetchAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const auth = await authHeaders();
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { ...auth, ...(init.headers as Record<string, string> | undefined) },
  });
  if (res.status === 401) await renovarSesion();
  return res;
}

async function errorDetail(res: Response): Promise<string> {
  let detail = `${res.status} ${res.statusText}`.trim();
  try {
    const j = await res.json();
    if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
  } catch {
    // ignore
  }
  return detail;
}

export type EscaneadaInfo = {
  existe: boolean;
  nombre_archivo?: string;
  subido_por?: string;
  subido_en?: string; // ISO
  tamanio?: number;
};

/** Emails de los empleados que ya tienen PDF de responsiva firmada. */
export type EstadoEscaneadas = {
  /** Tienen PDF de responsiva firmada. */
  conPdf: Set<string>;
  /** Tienen PDF, pero su equipo cambió después de subirlo (hay que actualizarla). */
  desactualizadas: Set<string>;
};

export async function getEscaneadasEmails(): Promise<EstadoEscaneadas> {
  const res = await fetchAuth("/api/equipos/responsivas-escaneadas");
  if (!res.ok) throw new Error(await errorDetail(res));
  const j: { emails: string[]; desactualizadas?: string[] } = await res.json();
  return { conPdf: new Set(j.emails), desactualizadas: new Set(j.desactualizadas ?? []) };
}

export async function getEscaneadaInfo(empleado: string): Promise<EscaneadaInfo> {
  const p = new URLSearchParams({ empleado });
  const res = await fetchAuth(`/api/equipos/responsiva-escaneada/info?${p}`);
  if (!res.ok) throw new Error(await errorDetail(res));
  return res.json();
}

export async function subirEscaneada(empleado: string, file: File): Promise<void> {
  const p = new URLSearchParams({ empleado });
  const body = new FormData();
  body.append("archivo", file);
  // OJO: no fijar Content-Type — el navegador pone el boundary del multipart.
  const res = await fetchAuth(`/api/equipos/responsiva-escaneada?${p}`, {
    method: "POST",
    body,
  });
  if (!res.ok) throw new Error(await errorDetail(res));
}

/** Abre el PDF escaneado en una pestaña nueva. */
export async function verEscaneada(empleado: string): Promise<void> {
  const p = new URLSearchParams({ empleado });
  const res = await fetchAuth(`/api/equipos/responsiva-escaneada?${p}`);
  if (!res.ok) throw new Error(await errorDetail(res));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function borrarEscaneada(empleado: string): Promise<void> {
  const p = new URLSearchParams({ empleado });
  const res = await fetchAuth(`/api/equipos/responsiva-escaneada?${p}`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) throw new Error(await errorDetail(res));
}
